/**
 * MarkerInteractions - Handles marker animations and interactions
 * Bounces map markers when hovering over listing cards
 * Scrolls to listings when hovering over markers
 */
export class MarkerInteractions {
	constructor(markerManager, options = {}) {
		this.markerManager = markerManager;
		this.initialized = false;
		this.scrollEnabled = false; // Cache for split-screen detection
		this.lastCheckTime = 0;
		this.checkInterval = 1000; // Only recheck every 1 second max
		this.markerHoverListenersAdded = false;
		this.isScrolling = false; // Flag to track if we're currently scrolling
		this.scrollOnHover = options.scrollOnHover !== false; // Default true
	}

	/**
	 * Initialize marker interactions
	 * Sets up hover event listeners on listing elements and markers
	 */
	init() {
		if (this.initialized) {
			return;
		}

		// Wait for DOM to be ready and markers to be loaded
		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', () => {
				this.setupListingHoverListeners();
				this.setupMarkerHoverListeners();
			});
		} else {
			this.setupListingHoverListeners();
			this.setupMarkerHoverListeners();
		}

		this.initialized = true;
	}

	/**
	 * Set up hover event listeners on listing elements
	 */
	setupListingHoverListeners() {
		// Standard GeoDirectory listings
		const standardListings = document.querySelectorAll('.geodir-category-list-view .geodir-post[data-post-id]');
		standardListings.forEach(listing => {
			const postId = listing.getAttribute('data-post-id');
			if (postId) {
				listing.addEventListener('mouseenter', () => this.animateMarker(postId));
				listing.addEventListener('mouseleave', () => this.stopMarkerAnimation(postId));
			}
		});

		// Elementor posts (extract post ID from class)
		const elementorPosts = document.querySelectorAll('.elementor-posts .elementor-post');
		elementorPosts.forEach(post => {
			const classList = post.className;
			const match = classList.match(/post-(\d+)/);
			if (match && match[1]) {
				const postId = match[1];
				post.addEventListener('mouseenter', () => this.animateMarker(postId));
				post.addEventListener('mouseleave', () => this.stopMarkerAnimation(postId));
			}
		});

	}

	/**
	 * Animate (bounce) a marker by post ID
	 * @param {string|number} postId - The post ID
	 */
	animateMarker(postId) {
		const marker = this.markerManager.getMarkerByPostId(postId);
		if (!marker) {
			return;
		}

		// For Leaflet markers with divIcon (custom HTML structure)
		if (marker._icon) {
			// If using divIcon with label, find the position-absolute div for bounce
			if (marker._icon.classList.contains('map-marker-wrapper')) {
				const iconDiv = marker._icon.querySelector('.position-absolute');
				if (iconDiv && !iconDiv.classList.contains('leaflet-marker-bounce')) {
					iconDiv.classList.add('leaflet-marker-bounce');
				}
				// Add highlighted class to wrapper
				marker._icon.classList.add('is-highlighted');
			}
			// Standard image icon (no label)
			else {
				if (!marker._icon.classList.contains('leaflet-marker-bounce')) {
					marker._icon.classList.add('leaflet-marker-bounce');
				}
			}
		}
	}

	/**
	 * Stop marker animation by post ID
	 * @param {string|number} postId - The post ID
	 */
	stopMarkerAnimation(postId) {
		const marker = this.markerManager.getMarkerByPostId(postId);
		if (!marker) {
			return;
		}

		// For Leaflet markers with divIcon (custom HTML structure)
		if (marker._icon) {
			// If using divIcon with label, find the position-absolute div to remove bounce
			if (marker._icon.classList.contains('map-marker-wrapper')) {
				const iconDiv = marker._icon.querySelector('.position-absolute');
				if (iconDiv) {
					iconDiv.classList.remove('leaflet-marker-bounce');
				}
				// Remove highlighted class from wrapper
				marker._icon.classList.remove('is-highlighted');
			}
			// Standard image icon (no label)
			else {
				marker._icon.classList.remove('leaflet-marker-bounce');
			}
		}
	}

	/**
	 * Set up hover event listeners on map markers
	 * Scrolls to listing and highlights it when hovering over a marker
	 */
	setupMarkerHoverListeners() {
		if (this.markerHoverListenersAdded) {
			return;
		}

		// Get all markers from the marker manager
		const markers = this.markerManager.markers;
		if (!markers || markers.length === 0) {
			return;
		}

		// Add hover listeners to each marker
		markers.forEach(marker => {
			// Get post ID from marker data
			const markerData = marker.options?.data;
			if (!markerData || !markerData.m) {
				return;
			}

			const postId = markerData.m;

			// Add mouseover listener
			marker.on('mouseover', () => {
				this.onMarkerHover(postId);
			});

			// Add mouseout listener
			marker.on('mouseout', () => {
				this.onMarkerLeave(postId);
			});
		});

		this.markerHoverListenersAdded = true;
	}

	/**
	 * Handle marker hover event
	 * Scrolls to listing and highlights it
	 * @param {string|number} postId - The post ID
	 */
	onMarkerHover(postId) {
		// Check if scroll on hover is enabled by widget setting
		if (this.scrollOnHover && this.shouldEnableScroll()) {
			this.scrollToListing(postId);
		}

		// Always highlight the listing
		this.highlightListing(postId);
	}

	/**
	 * Handle marker leave event
	 * Removes highlight from listing
	 * @param {string|number} postId - The post ID
	 */
	onMarkerLeave(postId) {
		this.unhighlightListing(postId);
	}

	/**
	 * Detect if scroll-to-listing should be enabled
	 * Uses cached result and only rechecks periodically
	 * @returns {boolean} True if scroll should be enabled
	 */
	shouldEnableScroll() {
		const now = Date.now();

		// Only recalculate if enough time has passed
		if (now - this.lastCheckTime > this.checkInterval) {
			this.scrollEnabled = this.detectSplitScreen();
			this.lastCheckTime = now;
		}

		return this.scrollEnabled;
	}

	/**
	 * Detect if map and listings are in split-screen layout
	 * @returns {boolean} True if split-screen layout detected
	 */
	detectSplitScreen() {
		// Check if map is in full window mode
		const mapContainer = this.markerManager.mapProvider.mapContainer;
		if (mapContainer && mapContainer.classList.contains('geodir-map-fullwindow')) {
			return false;
		}

		// Find the map container
		const mapWrap = document.querySelector('.geodir-map-wrap');
		if (!mapWrap) {
			return false;
		}

		// Find the listings container
		const listingsContainer = document.querySelector('.geodir-category-list-view, .elementor-posts');
		if (!listingsContainer) {
			return false;
		}

		// Get viewport dimensions
		const viewportHeight = window.innerHeight;
		const viewportWidth = window.innerWidth;

		// Mobile check - disable on small screens
		if (viewportWidth < 768) {
			return false;
		}

		// Get element positions
		const mapRect = mapWrap.getBoundingClientRect();
		const listingsRect = listingsContainer.getBoundingClientRect();

		// Check if BOTH are visible in viewport (at least partially)
		const mapVisible = mapRect.top < viewportHeight && mapRect.bottom > 0;
		const listingsVisible = listingsRect.top < viewportHeight && listingsRect.bottom > 0;

		if (!mapVisible || !listingsVisible) {
			return false;
		}

		// Check if they're stacked vertically
		const areStacked = mapRect.bottom < listingsRect.top || listingsRect.bottom < mapRect.top;

		if (areStacked) {
			return false;
		}

		return true;
	}

	/**
	 * Scroll to a listing element using multi-level strategy
	 * Tries to avoid moving the map at all costs
	 * @param {string|number} postId - The post ID
	 */
	scrollToListing(postId) {
		const listing = this.findListingElement(postId);
		if (!listing) {
			return;
		}

		// Level 1: Check if listing is already visible
		if (this.isListingVisible(listing)) {
			return;
		}

		// Level 2: Try to scroll within a scrollable container (best for sticky maps)
		const scrollableParent = this.findScrollableParent(listing);
		if (scrollableParent) {
			this.scrollWithinContainer(scrollableParent, listing);
			return;
		}

		// Level 3: Constrained page scroll that protects map position
		this.constrainedPageScroll(listing);
	}

	/**
	 * Check if a listing element is visible in the viewport
	 * @param {Element} listing - The listing element
	 * @returns {boolean} True if visible
	 */
	isListingVisible(listing) {
		const rect = listing.getBoundingClientRect();
		const viewportHeight = window.innerHeight;

		// Get sticky header height
		const stickyHeader = document.querySelector('.sticky-header, .fixed-top, #wpadminbar');
		const headerHeight = stickyHeader ? stickyHeader.offsetHeight : 0;

		// Check if listing is within visible viewport (accounting for header)
		const isVisible = rect.top >= headerHeight && rect.bottom <= viewportHeight;

		return isVisible;
	}

	/**
	 * Find a scrollable parent container for an element
	 * @param {Element} element - The element to search from
	 * @returns {Element|null} The scrollable parent or null
	 */
	findScrollableParent(element) {
		let parent = element.parentElement;

		while (parent && parent !== document.body) {
			const style = window.getComputedStyle(parent);
			const overflowY = style.overflowY;
			const overflow = style.overflow;

			// Check if this element is scrollable
			const isScrollable = (overflowY === 'auto' || overflowY === 'scroll' ||
			                     overflow === 'auto' || overflow === 'scroll');

			// Check if it actually has scrollable content
			const hasScrollableContent = parent.scrollHeight > parent.clientHeight;

			if (isScrollable && hasScrollableContent) {
				return parent;
			}

			parent = parent.parentElement;
		}

		return null;
	}

	/**
	 * Scroll within a container element (not the page)
	 * @param {Element} container - The scrollable container
	 * @param {Element} listing - The listing to scroll to
	 */
	scrollWithinContainer(container, listing) {
		const containerRect = container.getBoundingClientRect();
		const listingRect = listing.getBoundingClientRect();

		// Check if listing is already visible in container
		const isVisible = listingRect.top >= containerRect.top && listingRect.bottom <= containerRect.bottom;

		if (isVisible) {
			return;
		}

		// Calculate position relative to container
		const relativeTop = listingRect.top - containerRect.top;
		const scrollTo = container.scrollTop + relativeTop - 20; // 20px padding

		container.scrollTo({
			top: scrollTo,
			behavior: 'smooth'
		});
	}

	/**
	 * Simple page scroll that scrolls listing into view
	 * Scrolls both up and down to bring listing into viewport
	 * @param {Element} listing - The listing to scroll to
	 */
	constrainedPageScroll(listing) {
		const listingRect = listing.getBoundingClientRect();
		const viewportHeight = window.innerHeight;

		// Get sticky header height
		const stickyHeader = document.querySelector('.sticky-header, .fixed-top, #wpadminbar');
		const headerOffset = stickyHeader ? stickyHeader.offsetHeight : 0;

		// Check if listing is already fully visible in viewport
		const isFullyVisible = listingRect.top >= headerOffset && listingRect.bottom <= viewportHeight;

		if (isFullyVisible) {
			return;
		}

		// Calculate how much to scroll to bring listing into view
		let scrollAmount = 0;

		if (listingRect.top < headerOffset) {
			// Listing is above viewport - scroll up
			scrollAmount = listingRect.top - headerOffset - 20; // 20px padding from top
		} else if (listingRect.bottom > viewportHeight) {
			// Listing is below viewport - scroll down
			// Try to center it, but at minimum get it into view
			const listingHeight = listingRect.height;
			const availableSpace = viewportHeight - headerOffset;

			if (listingHeight < availableSpace) {
				// Listing fits in viewport - center it
				const centerOffset = (availableSpace - listingHeight) / 2;
				scrollAmount = listingRect.top - headerOffset - centerOffset;
			} else {
				// Listing is taller than viewport - just bring top into view
				scrollAmount = listingRect.top - headerOffset - 20;
			}
		}

		if (scrollAmount !== 0) {
			window.scrollBy({
				top: scrollAmount,
				behavior: 'smooth'
			});
		}
	}

	/**
	 * Highlight a listing element
	 * Adds 'is-highlighted' class to the card or listing item
	 * @param {string|number} postId - The post ID
	 */
	highlightListing(postId) {
		const listing = this.findListingElement(postId);
		if (!listing) {
			return;
		}

		// Check if listing has a child with class 'card'
		const card = listing.querySelector('.card');
		if (card) {
			// Add highlight to card
			card.classList.add('is-highlighted');
		} else {
			// Add highlight to listing item itself
			listing.classList.add('is-highlighted');
		}
	}

	/**
	 * Remove highlight from a listing element
	 * @param {string|number} postId - The post ID
	 */
	unhighlightListing(postId) {
		const listing = this.findListingElement(postId);
		if (!listing) {
			return;
		}

		// Remove from both card and listing item
		const card = listing.querySelector('.card');
		if (card) {
			card.classList.remove('is-highlighted');
		}
		listing.classList.remove('is-highlighted');

	}

	/**
	 * Find a listing element by post ID
	 * @param {string|number} postId - The post ID
	 * @returns {Element|null} The listing element or null
	 */
	findListingElement(postId) {
		// Try standard GeoDirectory listing
		let listing = document.querySelector(`.geodir-post[data-post-id="${postId}"]`);

		// Try Elementor post (uses class-based post ID)
		if (!listing) {
			listing = document.querySelector(`.elementor-post.post-${postId}`);
		}

		return listing;
	}
}
